import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const SessionCard = ({ session, expanded, onToggle }) => {
  const firstUserMsg = session.messages.find(m => m.role === 'user');
  return (
    <div className="chat-session-card" data-testid={`chat-session-${session.session_id}`}>
      <button className="chat-session-header" onClick={onToggle}>
        <div className="chat-session-meta">
          <span className="chat-session-preview">
            {firstUserMsg ? `"${firstUserMsg.content.slice(0, 80)}${firstUserMsg.content.length > 80 ? '…' : ''}"` : '(no visitor messages)'}
          </span>
          <span className="chat-session-sub">
            {formatWhen(session.last_at)} · {session.message_count} messages
          </span>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && (
        <div className="chat-session-transcript">
          {session.messages.map((m, i) => (
            <div key={`${session.session_id}-${i}`} className={`chat-transcript-msg ${m.role}`}>
              <span className="chat-transcript-role">{m.role === 'user' ? 'Visitor' : 'Ask Cal'}</span>
              <p>{m.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChatTab = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat-sessions`, {
        withCredentials: true
      });
      setSessions(response.data);
    } catch (err) {
      setError(err.response?.status === 401 || err.response?.status === 403
        ? 'Not authorized — please log in as admin.'
        : 'Could not load chat transcripts. Try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="chat-tab" data-testid="admin-chat-tab">
      <div className="chat-tab-header">
        <div>
          <h2><MessageCircle size={22} /> Ask Cal Conversations</h2>
          <p className="chat-tab-sub">What visitors are asking the chatbot — newest first. Click a conversation to read the full transcript.</p>
        </div>
        <button className="chat-refresh-btn" onClick={fetchSessions} data-testid="chat-refresh-btn">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {loading && <p className="chat-tab-status">Loading conversations…</p>}
      {error && <p className="chat-tab-status chat-tab-error">{error}</p>}
      {!loading && !error && sessions.length === 0 && (
        <p className="chat-tab-status">No conversations yet. Once visitors chat with Ask Cal, they'll show up here.</p>
      )}

      <div className="chat-sessions-list">
        {sessions.map((s) => (
          <SessionCard
            key={s.session_id}
            session={s}
            expanded={expandedId === s.session_id}
            onToggle={() => setExpandedId(expandedId === s.session_id ? null : s.session_id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatTab;
